/**
 * Writes the hidden inputs a dashboard submit carries, one setter per state
 * bucket.
 *
 * This is a plain class over the form element rather than a Stimulus controller
 * so that anything holding the element can write to the form. A host app mounts
 * its own extension controllers in its own Stimulus application, which cannot
 * look up a controller instance registered in the engine's application, so an
 * accessor that returned the charting-form controller instance would resolve to
 * null for any host-registered controller.
 */
export default class FormWriter {
  static formSelector = 'form[data-controller~="charting-form"]';

  /**
   * Dashboards can be nested, so bind to the closest form only.
   */
  static for(element) {
    const form = element?.closest(FormWriter.formSelector);

    return form ? new FormWriter(form) : null;
  }

  constructor(form) {
    this.form = form;
  }

  setVisual(namespace, key, value) {
    this.#setScalar('visual', `visuals[${FormWriter.#field(namespace, key)}]`, value);
  }

  setSort(namespace, member, direction) {
    const prefix = `sorts[${FormWriter.#field(namespace, member)}]`;

    this.#removeInputs('sort', prefix);
    this.#addInput(`${prefix}[direction]`, direction, 'sort');
  }

  setPagination(namespace, key, page, limit) {
    const prefix = `paginations[${FormWriter.#field(namespace, key)}]`;

    this.#removeInputs('pagination', prefix);
    this.#addInput(`${prefix}[page]`, page, 'pagination');

    if (Number(limit) > 0) {
      this.#addInput(`${prefix}[limit]`, limit, 'pagination');
    }
  }

  setFilter(namespace, member, operator, value) {
    const prefix = `filters[${FormWriter.#field(namespace, member)}]`;

    this.#removeInputs('filter', prefix);
    this.#addInput(`${prefix}[operator]`, operator, 'filter');

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        this.#addInput(`${prefix}[value][]`, entry, 'filter');
      });
    } else {
      this.#addInput(`${prefix}[value]`, value, 'filter');
    }
  }

  removeFilter(namespace, member) {
    this.#removeInputs('filter', `filters[${FormWriter.#field(namespace, member)}]`);
  }

  submit() {
    this.form.requestSubmit();
  }

  static #field(namespace, key) {
    return namespace ? `${namespace}:${key}` : key;
  }

  static #hiddenInput(name, value, target) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.dataset.chartingFormTarget = target;

    return input;
  }

  /**
   * Mirrors Stimulus target scoping: a nested dashboard has its own
   * charting-form, and its inputs belong to that form, not this one.
   */
  #inputs(target) {
    return Array.from(
      this.form.querySelectorAll(`[data-charting-form-target="${target}"]`)
    ).filter(
      (el) => el.closest(FormWriter.formSelector) === this.form
    );
  }

  #addInput(name, value, target) {
    this.form.appendChild(FormWriter.#hiddenInput(name, value, target));
  }

  /**
   * Reuses an existing input for the exact name when there is one, so a
   * repeated write updates in place rather than stacking duplicates.
   */
  #setScalar(target, name, value) {
    const existing = this.#inputs(target).find((el) => el.name === name);

    if (existing) {
      existing.value = value;
    } else {
      this.#addInput(name, value, target);
    }
  }

  #removeInputs(target, prefix) {
    this.#inputs(target)
      .filter((el) => el.name.startsWith(prefix))
      .forEach((el) => el.remove());
  }
}
